import {
  SupplierGetResponseType,
  SupplierStorefrontV2GetResponseType,
} from "@sellar-io/api-v2/types";
import { CalculatedDeliveryOffer } from "@sellar-io/common";
import { ReactElement, useCallback, useRef, useState } from "react";
import { useMatch } from "react-router";
import { useLocalStorage, useMediaQuery } from "usehooks-ts";
import { PaymentTermsCard } from "../../../../components/paymentTerms/PaymentTermsCard";
import {
  SkeletonContainer,
  SkeletonText,
} from "../../../../components/skeleton/Skeleton";
import { STOREFRONT_LINKS_PREVIEW } from "../../../../constants/routes";
import { ContextOrderBasketType } from "../../../../hooks";
import { useStorefrontLinkSearchStore } from "../../../storefrontLinks/StorefrontLinkSearchStore";
import { CheckoutSelectedFulfillmentType } from "../../StockList";
import { StorefrontBasketSummaryCard } from "./StorefrontCards/StorefrontBasketSummaryCard";
import { StorefrontDeliveryOffersCard } from "./StorefrontCards/StorefrontDeliveryOffersCard/StorefrontDeliveryOffersCard";
import { StorefrontDiscountCard } from "./StorefrontCards/StorefrontDiscountCard";
import { StorefrontDeliveryOptionsCard } from "./StorefrontCards/StorefrontOptions/StorefrontDeliveryOptionsCard";
import { StorefrontOrderRulesCard } from "./StorefrontCards/StorefrontOrderRulesCard";
import { StorefrontSupplierAWRSAndVATCard } from "./StorefrontCards/StorefrontSupplierAWRSAndVATCard";
import { useRemoteFeatureFlags } from "../../../../hooks/useRemoteFeatureFlags";
import { Button, Icon } from "@sellar-io/components";
import { DistributionEducationDialog } from "../../../discovery/v2/distribution/DistributionEducationDialog";
import { DistributionEducationDialogPage } from "../../../discovery/v2/distribution/DistributionEducationDialog.shared";
import { useStorefrontBuyerPostcode } from "./lib/useStorefrontBuyerPostcode";

export const StorefrontInformation = ({
  supplierStorefront,
  calculatedDeliveryOffers,
  supplier,
  order,
  orderTotal,
  orderSubtotal,
  orderDiscountAmount,
  onFulfillmentTypeChange,
  checkoutSelectedFulfillmentType,
}: {
  supplier: SupplierGetResponseType | undefined;
  supplierStorefront: SupplierStorefrontV2GetResponseType | undefined;
  currency: SupplierGetResponseType["currency"];
  calculatedDeliveryOffers: CalculatedDeliveryOffer[];
  order: ContextOrderBasketType | null | undefined;
  orderTotal: number;
  orderSubtotal: number;
  orderDiscountAmount: number;
  onFulfillmentTypeChange?: (
    fulfillmentType: CheckoutSelectedFulfillmentType,
  ) => void;
  checkoutSelectedFulfillmentType?: CheckoutSelectedFulfillmentType;
}): ReactElement => {
  const isDesktop = useMediaQuery("(min-width: 1280px)");
  const isLargeScreen = useMediaQuery("(min-width: 1921px)");
  const { selectedAudienceTab } = useStorefrontLinkSearchStore();
  const isStorefrontLinkPreview = !!useMatch(STOREFRONT_LINKS_PREVIEW);
  const hideDeliveryInformation =
    isStorefrontLinkPreview && selectedAudienceTab === "all";
  const [openDistributionEducationDialog, setOpenDistributionEducationDialog] =
    useState(false);
  const { buyerPostcode } = useStorefrontBuyerPostcode();
  const previousDefaultFulfillmentType = useRef<
    CheckoutSelectedFulfillmentType | undefined
  >(undefined);
  const getPreviousDefaultFulfillmentType = useCallback(
    () => previousDefaultFulfillmentType.current,
    [],
  );
  const setPreviousDefaultFulfillmentType = useCallback(
    (fulfillmentType: CheckoutSelectedFulfillmentType) => {
      previousDefaultFulfillmentType.current = fulfillmentType;
    },
    [],
  );

  const priceTierDiscounts =
    supplierStorefront?.VolumeDiscounts?.PriceTierDiscounts ?? [];

  const { isActive: isRemoteFlagActive } = useRemoteFeatureFlags();

  const palletOverrideEnabled = isRemoteFlagActive("courierHaulierEnforcement");

  const isImplicitProformaActive = isRemoteFlagActive("implicitProforma");

  const isImportSupplier = !!supplier?.onlyAllowCardPaymentWithDeposit;

  const canDisplayDistributionEducationTooltip =
    !!supplier &&
    checkoutSelectedFulfillmentType === "sellarDistribution" &&
    isDesktop &&
    !isImportSupplier;

  return (
    <>
      {isDesktop && !isLargeScreen && !isStorefrontLinkPreview && (
        <StorefrontBasketSummaryCard
          orderTotal={orderTotal}
          orderSubtotal={orderSubtotal}
          orderItems={order?.OrderItems ?? []}
          orderCurrency={order?.currency ?? "gbp"}
        />
      )}
      {supplierStorefront ? (
        <>
          {!palletOverrideEnabled && (
            <>
              <StorefrontDiscountCard
                orderDiscountAmount={orderDiscountAmount}
                orderCurrency={order?.currency ?? "gbp"}
                priceTierDiscounts={priceTierDiscounts}
                caseSizeRuleList={supplierStorefront.CaseSizeRules.list}
                className={"snap-center rounded-md xl:border"}
              />
              <StorefrontOrderRulesCard
                className={"snap-center rounded-md xl:border"}
                supplierStorefront={supplierStorefront}
                order={order}
                orderTotal={orderTotal}
              />
            </>
          )}
          {canDisplayDistributionEducationTooltip && (
            <DistributionStorefrontTooltip
              supplier={supplier}
              onOpenDistributionEducationDialog={() =>
                setOpenDistributionEducationDialog(true)
              }
            />
          )}
          {!hideDeliveryInformation && (
            <>
              {supplier && supplierStorefront ? (
                <>
                  <StorefrontDeliveryOptionsCard
                    getPreviousDefaultFulfillmentType={
                      getPreviousDefaultFulfillmentType
                    }
                    setPreviousDefaultFulfillmentType={
                      setPreviousDefaultFulfillmentType
                    }
                    onChange={onFulfillmentTypeChange}
                    checkoutSelectedFulfillmentType={
                      checkoutSelectedFulfillmentType
                    }
                    className={"snap-center rounded-md xl:border"}
                    supplier={supplier}
                    order={order}
                    supplierStorefront={supplierStorefront}
                    connection={supplierStorefront.SupplierRetailerConnection}
                    fulfillmentType={{
                      ...supplierStorefront.FulfillmentType,
                      fulfillmentType: supplierStorefront.FulfillmentType
                        .fulfillmentType as "direct" | "courier" | "haulier",
                    }}
                  />
                </>
              ) : null}
            </>
          )}
          <StorefrontDeliveryOffersCard
            className={"snap-center rounded-md xl:border"}
            supplierStorefront={supplierStorefront}
            calculatedDeliveryOffers={calculatedDeliveryOffers}
            order={order}
          />
          {palletOverrideEnabled && (
            <>
              <StorefrontDiscountCard
                orderDiscountAmount={orderDiscountAmount}
                orderCurrency={order?.currency ?? "gbp"}
                priceTierDiscounts={priceTierDiscounts}
                caseSizeRuleList={supplierStorefront.CaseSizeRules.list}
                className={"snap-center rounded-md xl:border"}
              />
              <StorefrontOrderRulesCard
                className={"snap-center rounded-md xl:border"}
                supplierStorefront={supplierStorefront}
                order={order}
                orderTotal={orderTotal}
              />
            </>
          )}
          {supplier && (
            <>
              <PaymentTermsCard
                className={"snap-center rounded-md xl:border"}
                connection={supplierStorefront.SupplierRetailerConnection}
                supplier={{
                  ...supplier,
                  CourierDeliverySchedule:
                    supplierStorefront.CourierDeliverySchedule ?? null,
                }}
                isImplicitProformaActive={isImplicitProformaActive}
              />
              <StorefrontSupplierAWRSAndVATCard supplier={supplier} />
            </>
          )}
        </>
      ) : (
        <SkeletonContainer>
          <div className="flex w-full flex-col gap-4">
            <div className="flex w-full flex-col gap-3 rounded-md border p-4">
              <SkeletonText className={"h-[25px] w-[180px] rounded-full"} />
              <SkeletonText className={"mt-4 w-[120px]"} />
              <SkeletonText className={"h-[25px] w-full border-dashed"} />
            </div>
            <div className="flex w-full flex-col gap-3 rounded-md border p-4">
              <SkeletonText className={"h-[25px] w-[180px] rounded-full"} />
              <SkeletonText className={"mt-4 w-[120px]"} />
              <SkeletonText className={"h-[25px] w-full border-dashed"} />
            </div>
            <div className="flex w-full flex-col gap-3 rounded-md border p-4">
              <SkeletonText className={"h-[25px] w-[180px] rounded-full"} />
              <SkeletonText className={"mt-4 w-[120px]"} />
              <SkeletonText className={"h-[25px] w-full border-dashed"} />
              <SkeletonText className={"h-[25px] w-[100px] border-dashed"} />
            </div>
          </div>
        </SkeletonContainer>
      )}
      {supplier && (
        <DistributionEducationDialog
          active={openDistributionEducationDialog}
          buyerPostcode={buyerPostcode}
          supplierId={supplier.id}
          initialPage={DistributionEducationDialogPage.EXPERT_DRAY_DELIVERY}
          onDismiss={() => setOpenDistributionEducationDialog(false)}
        />
      )}
    </>
  );
};

export const DistributionStorefrontTooltip = ({
  supplier,
  onOpenDistributionEducationDialog,
}: {
  supplier: SupplierGetResponseType;
  onOpenDistributionEducationDialog: () => void;
}) => {
  const supplierName = supplier.Business.name || "";

  const { isVisible, dismiss } = useDistributionEducationTooltipVisibility();

  if (!isVisible) {
    return null;
  }

  return (
    <>
      <div className="relative flex items-center gap-2 rounded-xl border border-[#8C23C7] bg-zinc-50 px-4 py-3 text-sm shadow-[2px_2px_16px_0px_#8C23C766]">
        <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 transform">
          <svg width="16" height="8" viewBox="0 0 16 8">
            <path
              d="M0 0 L8 8 L16 0"
              fill="white"
              stroke="#8C23C7"
              strokeWidth="1"
            />
          </svg>
        </div>

        <div>
          <Icon icon="INFORMATION_OUTLINE" />
        </div>
        <div className="flex flex-col gap-1">
          <span className="font-semibold">
            Get {supplierName} delivered by Sellar
          </span>
          <span className="text-zinc-700">
            Free, flexible dray delivery from our team of experts.
          </span>
          <div className="flex w-full items-center gap-2">
            <Button
              onClick={() => {
                onOpenDistributionEducationDialog();
                dismiss();
              }}
              color="slim"
              className="h-fit w-full flex-1"
            >
              Learn more
            </Button>
            <Button
              color="transparent"
              className="h-9 w-1/3 capitalize"
              onClick={() => dismiss()}
            >
              Got it
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

const useDistributionEducationTooltipVisibility = () => {
  const DISTRIBUTION_EDUCATION_TOOLTIP_KEY =
    "distributionEducationTooltipDismissed";
  const [isVisible, setIsVisible] = useLocalStorage<boolean>(
    DISTRIBUTION_EDUCATION_TOOLTIP_KEY,
    true,
  );
  return { isVisible, dismiss: () => setIsVisible(false) };
};
